/* ============================================================
 * RVRN Customer Vehicle Autofill — Global
 *
 * Persists the vehicle selected in the RVRN Wheel Finder to
 * localStorage (14-day rolling expiry, aligned with the Shopify
 * cart_ts cookie) and auto-fills any "Year / Make / Model" input
 * across the storefront: inquiry forms (Globo), popup forms,
 * product-page cart properties, etc.
 *
 * Matching is keyword-based — all three of year / make / model
 * must appear in name / placeholder / aria-label / id / associated
 * label text — so it is immune to third-party form id drift.
 * Third-party forms are injected dynamically, hence MutationObserver
 * + multi-trigger scanning instead of static selectors.
 *
 * No write-back: user edits in forms are never saved to storage.
 * ============================================================ */

(function () {
  'use strict';

  const STORAGE_KEY = 'rvrn_customer_vehicle';
  const TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days rolling, matches cart_ts
  const KEYWORDS = ['year', 'make', 'model'];
  const SCAN_WINDOW_MS = 8000; // MutationObserver capture window
  const SCAN_DEBOUNCE_MS = 150;

  const autoValues = new WeakMap(); // el -> last auto-filled value (system-owned)
  let isAutofilling = false; // guards the input listener against our own events
  let scanTimer = null;

  // ============================================================
  // Storage
  // ============================================================
  function saveVehicle(value) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        value: value,
        expiresAt: Date.now() + TTL_MS
      }));
    } catch (e) { /* private mode etc. — fail silently */ }
  }

  function readVehicle() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !data.value || typeof data.expiresAt !== 'number' || Date.now() > data.expiresAt) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return data.value;
    } catch (e) {
      return null;
    }
  }

  // ============================================================
  // Matching — keyword-based, immune to form id drift
  // ============================================================
  function hasAllKeywords(text) {
    if (!text) return false;
    const lower = String(text).toLowerCase();
    return KEYWORDS.every((kw) => lower.includes(kw));
  }

  function inputSignature(el) {
    const parts = [];
    if (el.name) parts.push(el.name);
    if (el.placeholder) parts.push(el.placeholder);
    const aria = el.getAttribute('aria-label');
    if (aria) parts.push(aria);
    if (el.id) {
      parts.push(el.id);
      const label = document.querySelector(`label[for="${el.id}"]`);
      if (label && label.textContent) parts.push(label.textContent);
    } else {
      const wrap = el.closest('label');
      if (wrap && wrap.textContent) parts.push(wrap.textContent);
    }
    return parts.join(' ');
  }

  function isEligibleInput(el) {
    const type = el.getAttribute('type');
    return !type || type.toLowerCase() === 'text';
  }

  // ============================================================
  // Fill
  // ============================================================
  function fillInput(el, value) {
    autoValues.set(el, value);
    isAutofilling = true;
    try {
      // Native setter + events so frameworks (Globo etc.) pick it up
      const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
      setter.call(el, value);
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    } catch (e) {
      el.value = value;
    } finally {
      isAutofilling = false;
    }
  }

  function scanAndFill() {
    const vehicle = readVehicle();
    if (!vehicle) return;
    document.querySelectorAll('input').forEach((el) => {
      if (!isEligibleInput(el)) return;                    // text inputs only
      if (!hasAllKeywords(inputSignature(el))) return;
      const hasValue = !!(el.value && el.value.trim() !== '');
      // Keep user-typed values untouched; only refresh system-owned
      // (auto-filled) or empty fields so vehicle changes propagate.
      if (hasValue && !autoValues.has(el)) return;
      fillInput(el, vehicle);
    });
  }

  function scheduleScan() {
    if (scanTimer) clearTimeout(scanTimer);
    scanTimer = setTimeout(scanAndFill, SCAN_DEBOUNCE_MS);
  }

  // ============================================================
  // Triggers
  // ============================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleScan);
  } else {
    scheduleScan();
  }

  document.addEventListener('click', scheduleScan, true);   // popups opening
  document.addEventListener('focusin', scheduleScan, true); // field focus

  // User typing takes ownership of a field — stop auto-updating it.
  // isAutofilling distinguishes our dispatched events from real input.
  document.addEventListener('input', (e) => {
    if (isAutofilling) return;
    if (autoValues.has(e.target)) autoValues.delete(e.target);
  }, true);

  // Finder broadcasts once Year/Make/Model are complete — save + fill now
  document.addEventListener('rvrn:vehicle-updated', (e) => {
    if (e.detail && e.detail.value) saveVehicle(e.detail.value);
    scheduleScan();
  });

  // Third-party forms (Globo/TPO) are injected dynamically — watch 8s
  function startObserver() {
    const startedAt = Date.now();
    const observer = new MutationObserver(() => {
      if (Date.now() - startedAt > SCAN_WINDOW_MS) {
        observer.disconnect();
        return;
      }
      scheduleScan();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  if (document.body) {
    startObserver();
  } else {
    document.addEventListener('DOMContentLoaded', startObserver);
  }
})();
